import React, { useMemo, useState, useCallback } from "react";
import MaInput from "../../../../Components/UI/ma-input";
import { InputAdornment } from "@mui/material";
import { MdAutoAwesome } from "react-icons/md";
import MaSearchableSelect from "../../../../Components/UI/ma-dropdown";

const REQ = (
  <span className="text-red-500" aria-hidden>
    {" "}
    *
  </span>
);

function normalizeTagPiece(s) {
  return String(s ?? "")
    .trim()
    .replace(/^#+/, "")
    .trim();
}

export function AddProductStepBasic({
  productName,
  onProductNameChange,
  categoryId,
  onCategoryIdChange,
  categoryOptions,
  categoriesLoading,
  productOriginId,
  onProductOriginIdChange,
  originOptions,
  originsLoading,
  description,
  onDescriptionChange,
  tags,
  onTagsChange,
}) {
  const [tagDraft, setTagDraft] = useState("");
  const nameLen = productName.length;
  const categories = categoryOptions ?? [];
  const origins = originOptions ?? [];

  const commitDraftAsTags = useCallback(() => {
    const raw = tagDraft.trim();
    if (!raw) {
      setTagDraft("");
      return;
    }
    const pieces = raw
      .split(/[,]+/)
      .map((p) => normalizeTagPiece(p))
      .filter(Boolean);
    if (!pieces.length) {
      setTagDraft("");
      return;
    }
    const next = [...tags];
    for (const p of pieces) {
      if (!next.some((t) => t.toLowerCase() === p.toLowerCase())) {
        next.push(p);
      }
    }
    onTagsChange(next);
    setTagDraft("");
  }, [tagDraft, tags, onTagsChange]);

  const removeTagAt = (index) => {
    onTagsChange(tags.filter((_, i) => i !== index));
  };

  const onTagInputKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      commitDraftAsTags();
      return;
    }
    if (e.key === "Backspace" && !tagDraft && tags.length > 0) {
      onTagsChange(tags.slice(0, -1));
    }
  };

  const categorySelectOptions = useMemo(
    () =>
      categories
        .map((c) => ({
          label: String(c?.name ?? "").trim() || "—",
          value: String(c?._id ?? ""),
        }))
        .filter((o) => o.value),
    [categories]
  );

  const originSelectOptions = useMemo(
    () =>
      origins
        .map((o) => ({
          label: String(o?.name ?? "").trim() || "—",
          value: String(o?._id ?? ""),
        }))
        .filter((o) => o.value),
    [origins]
  );

  return (
    <div className="flex flex-col gap-5 sm:gap-6">
      <div
        style={{ borderBottom: "1px solid #EAEAEA" }}
        className="px-4 pb-3 pt-4 sm:px-5 md:px-8 md:pt-0"
      >
        <h2 className="my-0 text-[16px] font-semibold text-[#1F2937] sm:text-[18px]">
          Basic Information
        </h2>
        <p className="my-0 mt-1 text-[13px] text-[#6B7280]">
          Fill in the core details buyers will see on your listing.
        </p>
      </div>

      <div className="px-4 pb-4 sm:px-5 md:px-8 md:pb-6">
        <MaInput
          label={
            <span>
              Product Name
              {REQ}
            </span>
          }
          fullWidth
          placeholder="e.g Basket"
          value={productName}
          onChange={(e) => onProductNameChange(e.target.value)}
          inputProps={{ maxLength: 80 }}
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <span className="text-[11px] text-[#9CA3AF] tabular-nums">
                    {nameLen}/80
                  </span>
                </InputAdornment>
              ),
            },
          }}
          borderColor="#DADADA"
        />

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 mt-3">
          <MaSearchableSelect
            label={
              <span>
                Category
                {REQ}
              </span>
            }
            options={categorySelectOptions}
            value={categoryId}
            onChange={onCategoryIdChange}
            disabled={categoriesLoading}
            placeholder={
              categoriesLoading ? "Loading…" : "Select category"
            }
            searchPlaceholder="Search categories…"
            emptyMessage={
              categoriesLoading ? "Loading…" : "No categories found."
            }
          />
          <MaSearchableSelect
            label={
              <span>
                Product Origin
                {REQ}
              </span>
            }
            options={originSelectOptions}
            value={productOriginId}
            onChange={onProductOriginIdChange}
            disabled={originsLoading}
            placeholder={
              originsLoading ? "Loading…" : "Select origin / country"
            }
            searchPlaceholder="Search origins…"
            emptyMessage={
              originsLoading ? "Loading…" : "No origins found."
            }
          />
        </div>

        <div className="relative [&_textarea]:pb-11 mt-3">
          <MaInput
            label={
              <span>
                Description
                {REQ}
              </span>
            }
            multiline
            minRows={5}
            fullWidth
            noHeight
            placeholder="Describe your product — material, dimensions, use case, story..."
            value={description}
            onChange={(e) => onDescriptionChange(e.target.value)}
            borderColor="#DADADA"
          />
          {/* <div className="absolute bottom-2.5 right-2.5 z-10 inline-flex items-center gap-1.5 rounded-full bg-[#F8F8F8] border border-solid border-[#EAEAEA80] px-3 py-1.5 text-[10px] font-semibold text-[#2E1834] transition-colors hover:bg-[#DDD6FE]">
            <MdAutoAwesome className="text-[12px]" aria-hidden />
            Generate with AI
          </div> */}
        </div>

        <div className="mt-3">
          <label className="mb-1.5 block text-sm font-medium text-[#374151]">
            Tags
          </label>
          <div className="flex min-h-[50px] flex-wrap items-center gap-2 rounded-lg border border-solid border-[#DADADA] bg-white px-3 py-2 transition-colors focus-within:border-[#2E1834] focus-within:ring-2 focus-within:ring-[#2E1834]/15">
            {tags.map((t, i) => (
              <span
                key={`${i}-${t}`}
                className="inline-flex max-w-full items-center gap-1 rounded-full bg-[#F3F0FA] py-1 pl-2.5 pr-1 text-[12px] font-medium text-[#2E1834]"
              >
                <span className="truncate">{t}</span>
                <button
                  type="button"
                  onClick={() => removeTagAt(i)}
                  className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[#6B7280] transition-colors hover:bg-white/80 hover:text-[#DC2626]"
                  aria-label={`Remove tag ${t}`}
                >
                  ×
                </button>
              </span>
            ))}
            <input
              type="text"
              className="min-h-[28px] min-w-[140px] flex-1 border-0 bg-transparent py-1 text-[13px] text-[#111827] outline-none placeholder:text-[#9CA3AF]"
              placeholder={
                tags.length
                  ? "Add another — Enter or comma"
                  : "Type a tag, press Enter or comma"
              }
              value={tagDraft}
              onChange={(e) => setTagDraft(e.target.value)}
              onKeyDown={onTagInputKeyDown}
              onBlur={commitDraftAsTags}
            />
          </div>
          <p className="mt-1 text-[12px] text-[#9CA3AF]">
            Add multiple tags to help buyers find this product in search.
          </p>
        </div>
      </div>
    </div>
  );
}
